import React, { useRef } from 'react';
import { FeatureGroup, Polygon, Tooltip } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';

function ringToCoords(latLngs) {
    const ring = Array.isArray(latLngs[0]) ? latLngs[0] : latLngs;
    return ring.map((p) => [p.lat, p.lng]);
}

/**
 * Renders one or more polygons. When editMode is true, wraps them in a single
 * FeatureGroup + leaflet-draw EditControl so the user can drag vertices on any
 * of them; edits are dispatched per polygon via onEdit(id, newCoords) so each
 * one can be routed to its own save target.
 *
 * Used in two places:
 *   - TractMapCard: a single focal polygon (with draw-when-empty + remove allowed).
 *   - WellMapCard:  every DRILLED_ON tract polygon, all editable as a group.
 *
 * polygons: Array<{
 *   id,                      // stable identifier; passed back in callbacks
 *   positions: [[lat,lng]…], // ring coords
 *   color, weight, opacity, fillOpacity, fillColor,
 *   label?: ReactNode,       // permanent tooltip content
 *   labelClassName?: string, // class on the permanent tooltip
 *   popup?: ReactNode,       // popup JSX
 * }>
 *
 * onEdit(id, newCoords):  fired once per polygon after the user finishes editing
 * onCreate(newCoords):    fired when the user draws a brand-new polygon. The
 *                         consumer decides what to do with the coords (route to a
 *                         specific target, prompt the user, etc.). Only available
 *                         when allowDraw is true — the consumer is responsible
 *                         for deciding when drawing is appropriate.
 * onClear(id):            fired when the user removes a polygon (local-only — the
 *                         consumer decides whether to propagate to a save)
 */
export default function EditablePolygonGroup({
    polygons = [],
    editMode = false,
    allowDraw = false,
    allowRemove = false,
    onEdit,
    onCreate,
    onClear,
    editControlPosition = 'topright',
}) {
    const fgRef = useRef(null);

    const setLayerRef = (id) => (el) => {
        if (el?.leafletElement) {
            el.leafletElement._polygonGroupId = id;
        }
    };

    const renderPolygon = (p) => (
        <Polygon
            key={p.id}
            ref={setLayerRef(p.id)}
            positions={p.positions}
            color={p.color}
            weight={p.weight}
            opacity={p.opacity}
            fillOpacity={p.fillOpacity}
            fillColor={p.fillColor}
        >
            {p.label && (
                <Tooltip permanent direction="center" className={p.labelClassName}>
                    {p.label}
                </Tooltip>
            )}
            {p.popup}
        </Polygon>
    );

    if (!editMode) {
        return <>{polygons.map(renderPolygon)}</>;
    }

    const handleEdited = (e) => {
        e.layers.eachLayer((layer) => {
            const id = layer._polygonGroupId;
            if (id == null || !onEdit) return;
            onEdit(id, ringToCoords(layer.getLatLngs()));
        });
    };

    const handleCreated = (e) => {
        if (!onCreate) return;
        const coords = ringToCoords(e.layer.getLatLngs());
        if (fgRef.current) {
            fgRef.current.leafletElement.removeLayer(e.layer);
        }
        onCreate(coords);
    };

    const handleDeleted = (e) => {
        if (!onClear) return;
        e.layers.eachLayer((layer) => {
            if (layer._polygonGroupId != null) onClear(layer._polygonGroupId);
        });
    };

    return (
        <FeatureGroup ref={fgRef} key={`epg-${polygons.length}`}>
            <EditControl
                position={editControlPosition}
                onEdited={handleEdited}
                onCreated={handleCreated}
                onDeleted={handleDeleted}
                draw={{
                    polygon: allowDraw ? { allowIntersection: false, showArea: false } : false,
                    rectangle: false,
                    circle: false,
                    circlemarker: false,
                    marker: false,
                    polyline: false,
                }}
                edit={{
                    edit: polygons.length > 0,
                    remove: allowRemove && polygons.length > 0,
                }}
            />
            {polygons.map(renderPolygon)}
        </FeatureGroup>
    );
}
